import { useRef, useState } from "react";
import { adminApi, errMsg } from "./api";
import { toast } from "../components/ui/sonner";

export const Field = ({ label, hint, children }) => (
  <div className="adm-field">
    {label && <label>{label} {hint && <span className="hint">— {hint}</span>}</label>}
    {children}
  </div>
);

export const TextInput = ({ value, onChange, type = "text", ...rest }) => (
  <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />
);

export const TextArea = ({ value, onChange, rows = 4, ...rest }) => (
  <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />
);

export const Toggle = ({ checked, onChange, label, testid }) => (
  <label className="adm-toggle" data-testid={testid}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

/* Array of strings editor */
export const StringListEditor = ({ items = [], onChange, placeholder = "Item", testid = "string-list" }) => {
  const update = (i, v) => onChange(items.map((it, j) => (j === i ? v : it)));
  return (
    <div className="list-editor" data-testid={testid}>
      {items.map((it, i) => (
        <div className="list-row" key={i}>
          <div className="fields"><input value={it} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} /></div>
          <button type="button" className="rm" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove">✕</button>
        </div>
      ))}
      <button type="button" className="adm-btn ghost sm" onClick={() => onChange([...items, ""])} data-testid={`${testid}-add`}>+ Add</button>
    </div>
  );
};

/* Array of objects editor, driven by field config [{key,label,type}] */
export const ObjectListEditor = ({ items = [], onChange, fields, testid = "obj-list" }) => {
  const update = (i, key, v) => onChange(items.map((it, j) => (j === i ? { ...it, [key]: v } : it)));
  const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));
  return (
    <div className="list-editor" data-testid={testid}>
      {items.map((it, i) => (
        <div className="list-row" key={i}>
          <div className="fields">
            {fields.map((f) =>
              f.type === "textarea" ? (
                <textarea key={f.key} rows={2} value={it[f.key] ?? ""} placeholder={f.label} onChange={(e) => update(i, f.key, e.target.value)} />
              ) : (
                <input key={f.key} value={it[f.key] ?? ""} placeholder={f.label} onChange={(e) => update(i, f.key, e.target.value)} />
              )
            )}
          </div>
          <button type="button" className="rm" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove">✕</button>
        </div>
      ))}
      <button type="button" className="adm-btn ghost sm" onClick={() => onChange([...items, { ...blank }])} data-testid={`${testid}-add`}>+ Add</button>
    </div>
  );
};

/* Upload a file to media library, returns media doc via onUploaded */
export const UploadButton = ({ onUploaded, accept = "image/*,.pdf,.doc,.docx", kind = "general", label = "Upload file", testid = "upload-btn" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await adminApi.post("/admin/media/upload", fd);
      toast.success(`Uploaded ${data.filename}`);
      onUploaded(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input type="file" ref={ref} accept={accept} style={{ display: "none" }} onChange={pick} data-testid={`${testid}-input`} />
      <button type="button" className="adm-btn ghost" onClick={() => ref.current?.click()} disabled={busy} data-testid={testid}>
        {busy ? "Uploading…" : label}
      </button>
    </>
  );
};

export const SaveBar = ({ onSave, saving, testid = "save-btn", label = "Save changes" }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
    <button className="adm-btn primary" onClick={onSave} disabled={saving} data-testid={testid}>
      {saving ? "Saving…" : label}
    </button>
  </div>
);
